<script setup lang="ts">
import { mdiChevronLeft, mdiChevronRight, mdiSortAlphabeticalVariant } from "@mdi/js";
import { storeToRefs } from "pinia";
import NavIcon from "../Nav/NavIcon.vue";
import t from "~/utils/translate";
import { ISport } from "~/types/sports/sport";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import { useSettings } from "~/store/settings";
import { useBetslip } from "~/store/betslip";
import { mediaLibrariesPath } from "~/utils/constants";
import { isMobile } from "~/utils/constants";
import Skeleton from "~/components/ui/Skeleton.vue";
import slugify from "~/utils/sport/slugify";

interface Props {
	sports: ISport[] | undefined;
	liveOnly?: boolean;
	isEsports: boolean;
}

const route = useRoute();
const props = defineProps<Props>();
const sportId = computed(() => route.params.sportId);
const settings = useSettings();
const { azMenu } = storeToRefs(settings);
const betSlip = useBetslip();
const { personalizedMenu } = storeToRefs(betSlip);
const colorMode = useColorMode();

const sport = computed(() => {
	if (props.sports?.[0]) {
		let val: ISport | undefined;
		if (sportId.value) {
			val = props.sports.find((x) => x.sportId === sportId.value);
		} else {
			val = props.sports[0];
		}
		scrollIntoView();
		return val;
	}
});

const filteredSports = computed(() => {
	if (props.liveOnly) {
		return props.sports?.filter((sport) => sport.hasLiveInPlayEvents);
	}

	if (props.isEsports) {
		return props.sports?.filter((sport) => sport.sportType === "Sport" && sport.shouldDisplayCarousel);
	}

	const defaultSports = props.sports
		?.filter((sport) => {
			if ((settings.isSportsPPC || settings.isDataFree) && sport.sportType !== "Sport") return false;
			if (!sport.shouldDisplayCarousel) return false;
			if (isMobile) return sport.displayType !== "DesktopOnly";
			return sport.displayType !== "MobileOnly";
		})
		.sort((a, b) => {
			if (a.sortIndex < b.sortIndex) return -1;
			if (a.sortIndex > b.sortIndex) return 1;
			if (a.sortIndex === b.sortIndex) return 0;

			return 0;
		});
	if (personalizedMenu.value.length > 0) {
		personalizedMenu.value.forEach((sportId) => {
			const sport = props.sports?.find((sport) => sport.sportId === sportId);
			if (sport && defaultSports) {
				defaultSports.push(sport);
			}
		});
	}

	return defaultSports;
});

const navButton = tw`w-6 h-12 top-0 absolute transition-all bg-light-50 dark:bg-dark-800 hidden md:flex shadow z-10  items-center justify-center cursor-pointer`;
const { scroll, hasMore, forceUpdate } = useScrollableActions("sport-games-list-scroller", 250);

const scrollIntoView = async () => {
	await setTimeout(() => {
		const activeTab = document.getElementById("sport-games-list-scroller")?.getElementsByClassName("sports-list-active-item")[0];
		if (activeTab) {
			activeTab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}, 100);
	setTimeout(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, 100);
	setTimeout(() => {
		forceUpdate();
	}, 250);
};

const handleSportClick = (item: ISport) => {
	if (item.redirectURL?.includes("betwayzero")) {
		window.location.href = item.redirectURL;
		return;
	}
	if (settings.isNativeApp && settings.hasNativeLobby && item.redirectURL?.includes("lobby"))
		return useNuxtApp().$messageHandler({
			message: "navigateTo",
			data: {
				path: item.redirectURL,
				vertical: item.name,
			},
		});

	scrollIntoView();
	const router = useRouter();

	if (item.redirectURL) return router.push(item.redirectURL);

	const isOutrightsOnly = item.hasOutrights && !item.hasHighlights;
	router.push(
		sportUrlBuilder({
			sport: item.sportId,
			liveOnly: props.liveOnly,
			fixtureType: isOutrightsOnly ? "outrights" : "highlights",
		})
	);
};

const gameName = (item: ISport) => {
	try {
		if (item.sportId) return t(item.sportId, { identifier: "sports.", ensureExists: true });
		return t(slugify(item.name), { identifier: "sports.", ensureExists: true });
	} catch (e) {
		return item.name;
	}
};
</script>

<template>
	<!-- Loading -->
	<div
		v-if="!sports"
		:class="[
			'flex overflow-hidden sticky left-0 gap-2 pt-2 md:pt-0 z-10 bg-light-200 dark:bg-dark-900 lg:pt-0 lg:top-[208px] h-[90px] md:h-[82px]',
			settings.isNativeApp ? 'top-0' : 'top-[112px] md:top-[165px]  lg:top-[208px]',
		]"
	>
		<div v-for="i in Array.from({ length: 15 }).keys()" :key="i" class="flex w-[50px] h-[50px] items-center justify-center mb-2">
			<Skeleton class="w-[50px] h-[50px] rounded-lg bg-light-500 dark:bg-dark-500" />
		</div>
	</div>
	<div
		v-else
		class="flex overflow-hidden sticky left-0 pt-2 md:pt-0 z-10 bg-light-200 dark:bg-dark-900 lg:pt-0 lg:top-[208px]"
		:class="{
			'top-0': settings.isNativeApp,
			'top-[112px] md:top-[165px]  lg:top-[208px]': !settings.isNativeApp && !settings.isDataFree,
			'top-[152px] md:top-[205px]  lg:top-[248px]': !settings.isNativeApp && settings.isDataFree,
		}"
	>
		<!-- A - Z -->
		<div class="mr-2 md:hidden">
			<div class="flex items-center justify-center cursor-pointer overflow-hidden bg-dark-700 rounded h-[50px] w-[50px]" @click="azMenu = !azMenu">
				<UiIcon :path="mdiSortAlphabeticalVariant" class="w-7 h-7 fill-light-50" />
			</div>
			<strong class="text-center flex whitespace-nowrap text-dark-600 dark:text-light-400 items-center justify-center text-[10px]">
				<span class="text-ellipsis overflow-hidden">{{ t("all-sports") }}</span>
			</strong>
		</div>
		<!-- List -->
		<div class="relative w-full overflow-hidden">
			<div v-show="hasMore.left" @click="scroll('left')" :class="[navButton, 'left-0 rounded-r-lg']">
				<UiIcon :path="mdiChevronLeft" />
			</div>
			<div v-show="hasMore.right" @click="scroll('right')" :class="[navButton, 'right-0 rounded-l-lg']">
				<UiIcon :path="mdiChevronRight" />
			</div>
			<!-- List Scroller -->
			<div id="sport-games-list-scroller" class="flex gap-2 items-center w-full mb-2 overflow-x-scroll overflow-y-hidden scrollbar-hidden">
				<!-- List Item -->
				<div v-for="item in filteredSports" class="w-[50px]">
					<button
						:key="item.sportId"
						@click="handleSportClick(item)"
						:class="[
							'flex w-[50px] h-[50px] items-center justify-center relative rounded-lg transition-all',
							item.sportId === sport?.sportId ? 'sports-list-active-item bg-identity-50 dark:bg-identity-900 shadow' : 'bg-light-50 dark:bg-dark-800',
						]"
					>
						<!-- Live Count -->
						<div
							v-if="item.liveInPlayCount"
							class="absolute top-[1px] whitespace-nowrap text-[10px] w-full font-bold leading-[10px] justify-center bg-identity-50 p-[2px] rounded-[7px] flex items-center gap-[2px] text-dark-700 dark:text-light-50 dark:bg-identity-900"
						>
							<span class="block w-2 h-2 rounded-full bg-identity-600" />
							<span>{{ t("live") }} {{ item.liveInPlayCount }} </span>
						</div>
						<DataFreeGuard>
							<NuxtImg class="w-auto h-[34px]" v-if="item.imageURL && item.imageURL !== ''" :src="item.imageURL" :alt="item.name + '' + t('icon')" />
							<NavIcon
								v-else
								:width="34"
								:height="34"
								:src="`/icons/${item.sportId.replace('esports-', '').replace('---', '-')}.png`"
								:provider="null"
								:fallback="
									colorMode.value === 'light'
										? `${useRuntimeConfig().public.kenticoDomain}/${mediaLibrariesPath}/fallback-dark.png`
										: `${useRuntimeConfig().public.kenticoDomain}/${mediaLibrariesPath}/fallback-light.png`
								"
								:alt="item.name"
							/>
						</DataFreeGuard>

						<strong
							v-if="settings.isDataFree"
							class="text-center flex whitespace-nowrap text-dark-600 dark:text-light-400 items-center justify-center text-[10px] w-[40px]"
						>
							<span class="text-ellipsis overflow-hidden">{{ gameName(item) }}</span>
						</strong>
					</button>
					<DataFreeGuard
						><strong class="text-center flex whitespace-nowrap text-dark-600 dark:text-light-400 items-center justify-center text-[10px]">
							<span class="text-ellipsis overflow-hidden">{{ gameName(item) }}</span>
						</strong></DataFreeGuard
					>
				</div>
			</div>
		</div>
	</div>
</template>
